import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import moment from "moment";
import "moment/locale/gu";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Plus, Search, Filter, ListFilter, ArrowUpDown, Eye, FileText, Pencil, Trash2, Lock,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Package, ShoppingCart, Box,
} from "lucide-react";
import { toast } from "react-toastify";
import useConfirmPopup from "../../context/useConfirmPopup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchProcessReceivesList,
  fetchProcessReceiveParties,
  fetchProcessReceiveItems,
  getProcessReceivePartyOptions,
  getProcessReceiveItemOptions,
  getProcessReceives,
  getTotalProcessReceives,
  getProcessReceivesFilter,
  setProcessReceivesFilter,
  getProcessReceivesLoading,
} from "../../features/processReceives/processReceivesSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import { GET, DELETE } from "../../services/axios.service";
import { exportModuleReport } from "../../services/reportExport.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import { route } from "../../static/route";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { ProcessReceiveProps, ProcessReceiveFilterType } from "../../types/processReceive";
import { ProcessDefinitionProps } from "../../types/processDefinition";

const listSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function" ? (filterSelectStyles.control as any)(base, state) : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
  singleValue: (base: any) => ({
    ...(typeof filterSelectStyles.singleValue === "function" ? (filterSelectStyles.singleValue as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  placeholder: (base: any) => ({
    ...(typeof filterSelectStyles.placeholder === "function" ? (filterSelectStyles.placeholder as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  input: (base: any) => ({ ...base, fontSize: "1rem" }),
};

const formatINR = (val: number) =>
  val.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Props = {
  processDefinition: ProcessDefinitionProps;
};

const ProcessReceiveList = ({ processDefinition }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();
  moment.locale("gu");

  const labels = t.processReceives;
  const createRoute = `${route.processReceivesCreate}?process=${processDefinition.slug}`;
  const editRoute = (id: number | string) => `${route.processReceivesEdit.replace(":id", String(id))}?process=${processDefinition.slug}`;
  const viewRoute = (id: number | string) => `${route.processReceivesView.replace(":id", String(id))}?process=${processDefinition.slug}`;

  const receives = useAppSelector(getProcessReceives);
  const totalReceives = useAppSelector(getTotalProcessReceives);
  const filter = useAppSelector(getProcessReceivesFilter);
  const loading = useAppSelector(getProcessReceivesLoading);
  const partyOptions = useAppSelector(getProcessReceivePartyOptions);
  const itemOptions = useAppSelector(getProcessReceiveItemOptions);
  const selectedBranch = useAppSelector(getSelectedBranch);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number>(0);
  const [selectedFilterParty, setSelectedFilterParty] = useState<{ value: number; label: string } | null>(null);
  const [selectedFilterItem, setSelectedFilterItem] = useState<{ value: number; label: string } | null>(null);

  const sortByOptions = [
    { value: "id", label: labels.sortOptions.id },
    { value: "date", label: labels.sortOptions.date },
    { value: "grand_total", label: labels.sortOptions.grandTotal },
  ];
  const sortOrderOptions = [
    { value: "asc", label: labels.sortOrderOptions.asc },
    { value: "desc", label: labels.sortOrderOptions.desc },
  ];

  const getTotalItems = (rec: ProcessReceiveProps): number => rec.items?.length || 0;
  const getTotalQuantity = (rec: ProcessReceiveProps): number => {
    if (typeof rec.total_quantity === "number") return rec.total_quantity;
    return (rec.items || []).reduce((s, i) => s + Number(i.qty || 0), 0);
  };

  const { register, control: formControl, handleSubmit, reset, getValues } = useForm<ProcessReceiveFilterType>({
    defaultValues: { ...filter },
  });

  const [isExporting, setIsExporting] = useState(false);

  const fetchList = () => {
    dispatch(fetchProcessReceivesList({
      process_definition_id: processDefinition.id,
      page, size: pageSize,
      search: filter.search,
      party_id: filter.party_id,
      item_id: filter.item_id,
      fromDate: filter.fromDate,
      toDate: filter.toDate,
      sortBy: filter.sortBy,
      sortOrder: filter.sortOrder,
    }));
  };

  const prevBranchIdRef = useRef<number | null>(null);
  const selectedBranchId = selectedBranch?.id ?? null;

  useEffect(() => {
    if (!selectedBranchId) return;

    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = selectedBranchId;

    const isBranchChange = prevBranchId !== null && prevBranchId !== selectedBranchId;
    if (isBranchChange && page !== 1) {
      setPage(1);
      return;
    }

    fetchList();
  }, [page, pageSize, filter, selectedBranchId, processDefinition.id]);

  useEffect(() => {
    dispatch(fetchProcessReceiveParties(processDefinition.id));
    dispatch(fetchProcessReceiveItems());
  }, [dispatch, processDefinition.id]);

  const onApplyFilters = (data: ProcessReceiveFilterType) => {
    dispatch(setProcessReceivesFilter({
      search: data.search || null,
      party_id: selectedFilterParty?.value || null,
      item_id: selectedFilterItem?.value || null,
      fromDate: data.fromDate || null,
      toDate: data.toDate || null,
      sortBy: data.sortBy,
      sortOrder: data.sortOrder,
    }));
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults: ProcessReceiveFilterType = {
      search: null, party_id: null, item_id: null,
      fromDate: null, toDate: null, sortBy: "id", sortOrder: "desc",
    };
    reset(defaults);
    setSelectedFilterParty(null);
    setSelectedFilterItem(null);
    dispatch(setProcessReceivesFilter(defaults));
    setPage(1);
  };

  const onExportReport = () => {
    const values = getValues();
    exportModuleReport(
      "/v1/process-receives-report-pdf",
      {
        process_definition_id: processDefinition.id,
        search: values.search || null,
        party_id: selectedFilterParty?.value || null,
        item_id: selectedFilterItem?.value || null,
        fromDate: values.fromDate || null,
        toDate: values.toDate || null,
        sortBy: values.sortBy,
        sortOrder: values.sortOrder,
      },
      toastStyles,
      setIsExporting,
      labels.dateRangeRequired,
    );
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirmDelete(
      labels.deleteConfirmText,
      labels.deleteConfirmTitle,
      labels.deleteConfirmButton
    );
    if (!confirmed) return;
    setActionLoadingId(id);
    try {
      const response: any = await DELETE(`/v1/process-receives/${id}`);
      if (response.data.status === "success") {
        toast.success(response.data.message, { ...(toastStyles as any) });
        const newPage = receives.length > 1 ? page : Math.max(page - 1, 1);
        newPage === page ? fetchList() : setPage(newPage);
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
    setActionLoadingId(0);
  };

  const handleDownloadPdf = async (id: number) => {
    setActionLoadingId(id);
    try {
      const response: any = await GET(`/v1/process-receives/${id}/pdf`, {});
      if (response.data.status === "success") {
        const downloadLink = response.data.data.download_link;
        try {
          const newWindow = window.open(downloadLink, "_blank");
          if (!newWindow || newWindow.closed || typeof newWindow.closed == "undefined") {
            throw new Error("Popup blocked");
          }
        } catch (e) {
          window.location.href = downloadLink;
        }
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
    setActionLoadingId(0);
  };

  const totalPages = Math.ceil(totalReceives / pageSize);
  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalReceives === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalReceives);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-3 ml-auto">
          <Button variant="outline" className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="h-4 w-4 mr-2" />{labels.filterToggle}
          </Button>
          {canAccess("process-receives.create") && processDefinition.has_receive && (
            <Button className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm" onClick={() => navigate(createRoute)}>
              <Plus className="h-4 w-4 mr-2" />{labels.addReceiveButton}
            </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <Card>
          <CardHeader><CardTitle className="text-lg">{labels.filterTitle}</CardTitle></CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">{labels.partyLabel}</label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <ReactSelect styles={listSelectStyles} options={partyOptions} value={selectedFilterParty}
                      onChange={(opt: any) => setSelectedFilterParty(opt)} placeholder={labels.selectParty} isClearable />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">{labels.itemLabel}</label>
                  <div className="relative">
                    <Box className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <ReactSelect styles={listSelectStyles} options={itemOptions} value={selectedFilterItem}
                      onChange={(opt: any) => setSelectedFilterItem(opt)} placeholder={labels.itemLabel} isClearable />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">{labels.searchLabel}</label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A]" />
                    <Input className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand" placeholder={labels.searchPlaceholder} {...register("search")} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">{labels.fromDateLabel}</label>
                  <Input type="date" className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand" {...register("fromDate")} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">{labels.toDateLabel}</label>
                  <Input type="date" className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand" {...register("toDate")} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">{labels.sortByLabel}</label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller name="sortBy" control={formControl} render={({ field }) => (
                      <ReactSelect styles={listSelectStyles} options={sortByOptions}
                        value={sortByOptions.find((o) => o.value === field.value)}
                        onChange={(opt: any) => field.onChange(opt?.value || "id")} />
                    )} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">{labels.sortOrderLabel}</label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller name="sortOrder" control={formControl} render={({ field }) => (
                      <ReactSelect styles={listSelectStyles} options={sortOrderOptions}
                        value={sortOrderOptions.find((o) => o.value === field.value)}
                        onChange={(opt: any) => field.onChange(opt?.value || "desc")} />
                    )} />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button type="button" variant="outline" className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm" onClick={handleClearFilters}>
                  {labels.clearFilters}
                </Button>
                <Button type="button" disabled={isExporting} onClick={onExportReport} className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm">
                  {isExporting ? "..." : labels.exportReport}
                </Button>
                <Button type="submit" className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm">
                  {labels.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="md:hidden space-y-3">
        {loading && receives.length === 0 ? (
          <Card><CardContent className="py-8"><div className="flex items-center justify-center gap-2 text-gray-500"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />{labels.loading}</div></CardContent></Card>
        ) : !loading && receives.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-gray-500">{labels.noReceivesFound}</CardContent></Card>
        ) : (
          <div className={`space-y-3${loading ? " opacity-50 pointer-events-none" : ""}`}>
            {receives.map((rec: ProcessReceiveProps, index: number) => (
              <Card key={rec.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-400">#{(page - 1) * pageSize + index + 1}</span>
                      <div className="flex items-center gap-1.5">
                        <ShoppingCart className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-gray-800 text-sm">{rec.doc_no || labels.receiveIdLabel}</div>
                          <div className="text-xs text-gray-500">ID: {rec.id}</div>
                        </div>
                      </div>
                    </div>
                    {canAccess("money-view") && (
                      <div className="text-right">
                        <div className="font-bold text-[#113F67]">₹{formatINR(Number(rec.grand_total))}</div>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t">
                    <Badge className="mb-1.5 text-[11px]" variant={rec.mode === "outsource" ? "default" : "secondary"}>
                      {rec.mode === "outsource" ? t.processReceives.outsourceLabel : t.processReceives.inHouseLabel}
                    </Badge>
                    {rec.party_id ? (
                      <Link to={route.partiesView.replace(":id", String(rec.party_id))} target="_blank" rel="noopener noreferrer" className="block hover:underline">
                        <div className="font-semibold text-[#113F67]">{rec.party_firm_name || rec.party_name || "—"}</div>
                        <div className="text-xs text-gray-500">{rec.party_phone ? `(${rec.party_phone})` : ""}</div>
                      </Link>
                    ) : (
                      <div className="text-sm text-gray-500">{rec.party_firm_name || rec.party_name || "—"}</div>
                    )}
                  </div>

                  <div className="mt-2 pt-2 border-t space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{labels.table.totalQty}</span>
                      <span className="text-gray-700 font-medium">{getTotalItems(rec)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{labels.totalQty}</span>
                      <span className="text-gray-700 font-medium">{getTotalQuantity(rec)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{labels.table.date}</span>
                      <div className="text-right">
                        <div className="text-gray-700">{moment(rec.date).format("DD-MM-YYYY")}</div>
                        <div className="text-xs text-gray-400">{moment(rec.created_at).format("DD-MM-YYYY hh:mm A")}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-2 pt-2 border-t gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10" onClick={() => navigate(viewRoute(rec.id))}>
                      <Eye className="h-4 w-4 text-[#113F67]" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-orange-50" onClick={() => handleDownloadPdf(rec.id)}>
                      <FileText className="h-4 w-4 text-orange-500" />
                    </Button>
                    {canAccess("process-receives.update") && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={rec.can_edit === false ? labels.cantEditAfterReceive : t.common.edit}
                        disabled={rec.can_edit === false}
                        onClick={() => rec.can_edit !== false && navigate(editRoute(rec.id))}
                      >
                        {rec.can_edit === false ? <Lock className="h-4 w-4 text-gray-400" /> : <Pencil className="h-4 w-4 text-[#113F67]" />}
                      </Button>
                    )}
                    {canAccess("process-receives.delete") && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={rec.can_edit === false ? labels.cantDeleteAfterReceive : t.common.delete}
                        disabled={rec.can_edit === false}
                        onClick={() => rec.can_edit !== false && handleDelete(rec.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">{labels.table.srNo}</TableHead>
                  <TableHead>{labels.receiveIdLabel}</TableHead>
                  <TableHead>{labels.table.party}</TableHead>
                  <TableHead className="text-center">{labels.table.totalQty}</TableHead>
                  {canAccess("money-view") && <TableHead className="text-right">{labels.table.grandTotal}</TableHead>}
                  <TableHead>{labels.table.date}</TableHead>
                  <TableHead className="text-right">{labels.table.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={canAccess("money-view") ? 7 : 6} className="text-center py-8"><div className="flex items-center justify-center gap-2 text-gray-500"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />{labels.loading}</div></TableCell></TableRow>
                ) : receives.length === 0 ? (
                  <TableRow><TableCell colSpan={canAccess("money-view") ? 7 : 6} className="text-center py-8 text-gray-500">{labels.noReceivesFound}</TableCell></TableRow>
                ) : (
                  receives.map((rec: ProcessReceiveProps, index: number) => (
                    <TableRow key={rec.id} className="align-top">
                      <TableCell className="font-medium text-gray-500">{(page - 1) * pageSize + index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <ShoppingCart className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-gray-800 text-sm">{rec.doc_no || labels.receiveIdLabel}</div>
                            <div className="text-xs text-gray-500">ID: {rec.id}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="mb-1 text-[11px]" variant={rec.mode === "outsource" ? "default" : "secondary"}>
                          {rec.mode === "outsource" ? t.processReceives.outsourceLabel : t.processReceives.inHouseLabel}
                        </Badge>
                        {rec.party_id ? (
                          <Link to={route.partiesView.replace(":id", String(rec.party_id))} target="_blank" rel="noopener noreferrer" className="block hover:underline">
                            <div className="font-semibold text-[#113F67]">{rec.party_firm_name || rec.party_name || "—"}</div>
                            <div className="text-xs text-gray-500">{rec.party_phone ? `(${rec.party_phone})` : ""}</div>
                          </Link>
                        ) : (
                          <div className="text-sm text-gray-500">{rec.party_firm_name || rec.party_name || "—"}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-medium text-gray-700">{getTotalQuantity(rec)}</TableCell>
                      {canAccess("money-view") && (
                        <TableCell className="text-right font-semibold text-[#113F67]">₹{formatINR(Number(rec.grand_total))}</TableCell>
                      )}
                      <TableCell className="text-sm">
                        <div className="text-gray-700">{moment(rec.date).format("DD-MM-YYYY")}</div>
                        <div className="text-xs text-gray-400">{moment(rec.created_at).format("DD-MM-YYYY hh:mm A")}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoadingId === rec.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67] ml-auto" />
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10" title={t.common.view} onClick={() => navigate(viewRoute(rec.id))}>
                              <Eye className="h-4 w-4 text-[#113F67]" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-orange-50" title={t.common.downloadPdf} onClick={() => handleDownloadPdf(rec.id)}>
                              <FileText className="h-4 w-4 text-orange-500" />
                            </Button>
                            {canAccess("process-receives.update") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                                title={rec.can_edit === false ? labels.cantEditAfterReceive : t.common.edit}
                                disabled={rec.can_edit === false}
                                onClick={() => rec.can_edit !== false && navigate(editRoute(rec.id))}
                              >
                                {rec.can_edit === false ? <Lock className="h-4 w-4 text-gray-400" /> : <Pencil className="h-4 w-4 text-[#113F67]" />}
                              </Button>
                            )}
                            {canAccess("process-receives.delete") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                title={rec.can_edit === false ? labels.cantDeleteAfterReceive : t.common.delete}
                                disabled={rec.can_edit === false}
                                onClick={() => rec.can_edit !== false && handleDelete(rec.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {!loading && receives.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>{start}-{end} of {totalReceives}</span>
            <span>{labels.pageSizeLabel}</span>
            <Select value={String(pageSize)} onValueChange={(val) => { setPageSize(Number(val)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(1)} disabled={page === 1}><ChevronsLeft className="h-4 w-4" /></Button>
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}><ChevronLeft className="h-4 w-4" /></Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? <span key={`e-${i}`} className="px-2 text-gray-400">...</span> : (
                <Button key={p} variant={page === p ? "default" : "outline"} className={`w-8 h-8 p-0 ${page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""}`} onClick={() => setPage(p as number)}>{p}</Button>
              )
            )}
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(totalPages)} disabled={page === totalPages}><ChevronsRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessReceiveList;
